import { Compass } from "lucide-react";
import { useCallback } from "react";

import { useOpenPlaygroundTourInNewTab } from "./hooks/useOpenPlaygroundTourInNewTab";
import { useTourUrlParam } from "./hooks/useTourUrlParam";
import { posthogTrackTourStarted } from "./posthog";

import { useOptionalPlaygroundTourContext } from "@/components/Playground/tour/context";
import { Button, ButtonProps, ButtonSize } from "@/components/ui/button";
import { BasicTooltip } from "@/components/ui/Tooltip";

const DEFAULT_BUTTON_CLASS =
  "flex items-center gap-2 rounded-full transition-all duration-200 hover:bg-gray-100 active:scale-95";

interface PlaygroundTourTriggerProps {
  variant?: ButtonProps["variant"];
  size?: ButtonSize;
  className?: string;
}

const TooltipButton = ({
  onClick,
  variant = "ghost",
  size,
  className,
}: PlaygroundTourTriggerProps & { onClick: () => void }) => (
  <BasicTooltip content="Take a tour of the Playground">
    <Button
      variant={variant}
      size={size}
      className={className ?? DEFAULT_BUTTON_CLASS}
      onClick={onClick}
    >
      <Compass className="h-4 w-4" />
      <span>Tour</span>
    </Button>
  </BasicTooltip>
);

/**
 * In-page tour trigger: only renders inside a `PlaygroundTourProvider`.
 * Adds `?tour` to the current URL; the `useTourUrlParam` hook on the page
 * picks it up and starts the tour.
 */
const InPagePlaygroundTourTrigger = (props: PlaygroundTourTriggerProps) => {
  const { addTourParam } = useTourUrlParam();
  const handleClick = useCallback(() => {
    posthogTrackTourStarted({ source: "playground-header" });
    addTourParam();
  }, [addTourParam]);
  return <TooltipButton {...props} onClick={handleClick} />;
};

/**
 * New-tab tour trigger: opens `/workspace/:workspaceId/playground?tour` in
 * a new browser tab. Used outside the tour provider (e.g. on the
 * PromptTemplate page) so the user's saved-prompt context is preserved.
 */
const NewTabPlaygroundTourTrigger = (props: PlaygroundTourTriggerProps) => {
  const openInNewTab = useOpenPlaygroundTourInNewTab();
  return <TooltipButton {...props} onClick={openInNewTab} />;
};

/**
 * Auto-detects whether it is rendered inside a `PlaygroundTourProvider`:
 * - Inside the provider (standalone playground page) → in-page trigger.
 * - Outside the provider (PromptTemplate page) → new-tab trigger.
 */
const PlaygroundTourTrigger = (props: PlaygroundTourTriggerProps) => {
  const tour = useOptionalPlaygroundTourContext();
  if (tour) {
    return <InPagePlaygroundTourTrigger {...props} />;
  }
  return <NewTabPlaygroundTourTrigger {...props} />;
};

export default PlaygroundTourTrigger;
